import React, {useEffect, useState} from 'react';
import {Link} from "react-router-dom";
import {DownloadOutlined, EyeOutlined, ReloadOutlined} from '@ant-design/icons';
import {Button, Card, Col, DatePicker, Input, message, Row, Select, Space, Table, Tag, Tooltip} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {Dayjs} from "dayjs";

import {FactorySession, User} from "types";
import {FactoryService, UsersService} from "services";
import {GetFactorySessionsParams} from "services/factory";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import {
    downloadCsv,
    FAMILY_OPTIONS,
    formatDate,
    getFailureBinLabel,
    getFamilyLabel,
    getSessionDate,
    getStatusColor,
    getWorkerLabel,
    OVERALL_OPTIONS,
    sessionsToCsv
} from "../helpers";

// ----- Global variables -----
const DEFAULT_PAGE_SIZE = 20
const EXPORT_PAGE_SIZE = 500
// Roles allowed to use the device tester
const TESTER_ROLES = ['factory-worker', 'support', 'admin', 'super-admin', 'root']

const {RangePicker} = DatePicker;
const {Search} = Input;

// ----- Types -----
type Filters = Omit<GetFactorySessionsParams, 'page' | 'pageSize'>

// ----- Components -----
const FactorySessionsListPage: React.FC = () => {
    // ----- Data -----
    const [sessions, setSessions] = useState<FactorySession[]>([]);
    const [loading, setLoading] = useState<boolean>(false)
    const [exporting, setExporting] = useState<boolean>(false)
    const [workers, setWorkers] = useState<User[]>([])
    const [loadingWorkers, setLoadingWorkers] = useState<boolean>(false)

    // ----- Table -----
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [filters, setFilters] = useState<Filters>({})

    // ----- Init -----
    useEffect(() => {
        fetchWorkers()
    }, [])

    useEffect(() => {
        fetchData()
    }, [pageSize, currentPage, filters])

    const fetchWorkers = () => {
        setLoadingWorkers(true)
        UsersService.getStaff()
            .then(res => {
                const users = res?.data?.users || []
                setWorkers(users.filter((user: User) => TESTER_ROLES.includes(user?.role || '')))
            })
            .catch(err => {
                console.error(err)
            })
            .finally(() => {
                setLoadingWorkers(false)
            })
    }

    const fetchData = () => {
        setLoading(true)
        FactoryService.getFactorySessions({
            page: currentPage,
            pageSize,
            ...filters,
        })
            .then(res => {
                setSessions(res?.data?.items || [])
                setTotalItems(res?.data?.count || 0);
            })
            .catch(err => {
                console.error(err)
                message.error('Error while getting factory sessions')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // ----- Handlers -----
    const updateFilters = (newFilters: Filters) => {
        setCurrentPage(1)
        setFilters(currentFilters => ({...currentFilters, ...newFilters}))
    }

    const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        updateFilters({
            dateFrom: dates?.[0] ? dates[0].startOf('day').toISOString() : null,
            dateTo: dates?.[1] ? dates[1].endOf('day').toISOString() : null,
        })
    }

    // Export every session matching the current filters, not just the visible page
    const handleExport = async () => {
        setExporting(true)
        try {
            const allSessions: FactorySession[] = []
            let page = 1
            let count = 0
            do {
                const res = await FactoryService.getFactorySessions({...filters, page, pageSize: EXPORT_PAGE_SIZE})
                const items = res?.data?.items || []
                count = res?.data?.count || 0
                allSessions.push(...items)
                if (!items.length) break
                page++
            } while (allSessions.length < count)

            downloadCsv(sessionsToCsv(allSessions), `factory-sessions-${new Date().toISOString().slice(0, 10)}.csv`)
        } catch (err) {
            console.error(err)
            message.error('Error while exporting factory sessions')
        } finally {
            setExporting(false)
        }
    }

    const workerOptions = workers.map((user: User) => ({
        label: `${user.email} - ${user.role}`,
        value: user.id,
    }))

    const columns: ColumnsType<FactorySession> = [
        {
            title: 'Date',
            key: 'date',
            render: (_, record) => formatDate(getSessionDate(record)),
        },
        {
            title: 'Worker',
            key: 'worker',
            render: (_, record) => getWorkerLabel(record),
        },
        {
            title: 'Family',
            dataIndex: 'family',
            key: 'family',
            render: (family: string) => getFamilyLabel(family),
        },
        {
            title: 'Serial / Bluetooth id',
            key: 'serial',
            render: (_, record) => record.device?.serial ||
                (record.device?.bluetoothId &&
                    <Tooltip title="No serial, Bluetooth id">
                        <span style={{color: '#888'}}>{record.device.bluetoothId}</span>
                    </Tooltip>),
        },
        {
            title: 'Firmware',
            key: 'firmware',
            render: (_, record) => record.device?.firmwareVersion,
        },
        {
            title: 'Overall',
            dataIndex: 'overall',
            key: 'overall',
            render: (overall: string) => <Tag color={getStatusColor(overall)}>{overall?.toUpperCase()}</Tag>,
        },
        {
            title: 'Failure bin',
            dataIndex: 'failureBin',
            key: 'failureBin',
            render: (failureBin: string | null, record) =>
                <Tooltip title={record.failureNote}>{getFailureBinLabel(failureBin)}</Tooltip>,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Tooltip title="Details page">
                    <Link to={`/factory/sessions/${record.id}`}>
                        <Button type="primary" icon={<EyeOutlined/>}/>
                    </Link>
                </Tooltip>
            )
        }
    ];

    return (
        <>
            <CustomBreadcrumb list={['factory tests', 'list']}/>
            <Card title="Factory test sessions"
                  extra={
                      <Space>
                          <Button loading={exporting}
                                  disabled={!totalItems}
                                  onClick={handleExport}
                                  icon={<DownloadOutlined/>}>
                              Export CSV
                          </Button>
                          <Button loading={loading}
                                  onClick={() => fetchData()}
                                  icon={<ReloadOutlined/>}/>
                      </Space>
                  }
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col span={6}>
                        <Select placeholder="Worker"
                                style={{width: '100%'}}
                                loading={loadingWorkers}
                                options={workerOptions}
                                showSearch
                                optionFilterProp="label"
                                allowClear
                                onChange={(workerId?: number) => updateFilters({workerId: workerId || null})}
                        />
                    </Col>
                    <Col span={4}>
                        <Select placeholder="Family"
                                style={{width: '100%'}}
                                options={FAMILY_OPTIONS}
                                allowClear
                                onChange={(family?: string) => updateFilters({family: family || null})}
                        />
                    </Col>
                    <Col span={4}>
                        <Select placeholder="Overall"
                                style={{width: '100%'}}
                                options={OVERALL_OPTIONS}
                                allowClear
                                onChange={(overall?: string) => updateFilters({overall: overall || null})}
                        />
                    </Col>
                    <Col span={4}>
                        <Search placeholder="Serial (prefix)"
                                allowClear
                                onSearch={(serial: string) => updateFilters({serial: serial.trim() || null})}
                        />
                    </Col>
                    <Col span={6}>
                        <RangePicker style={{width: '100%'}} onChange={handleDateRangeChange}/>
                    </Col>
                    <Col span={24}>
                        <Table<FactorySession>
                            rowKey="id"
                            columns={columns}
                            dataSource={sessions}
                            loading={loading}
                            size="small"
                            bordered
                            pagination={{
                                pageSize: pageSize,
                                current: currentPage,
                                total: totalItems,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '30', '50', '100'],
                                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`
                            }}
                            onChange={(pagination) => {
                                setPageSize(pagination.pageSize || DEFAULT_PAGE_SIZE);
                                setCurrentPage(pagination.current || 1);
                            }}
                        />
                    </Col>
                </Row>
            </Card>
        </>
    )
};

export default FactorySessionsListPage;
